import { IsoDateFormatter } from '~/utility/date/IsoDateFormatter'

/**
 * Calculate a 4-month date range centered around a target date
 * Fetches: previous month, current month, next month, and next+1 month
 * This provides an extra month buffer when users navigate forward
 *
 * @param centerDate - The date to center the range around (arrive date or today)
 * @returns Object with start and end dates for the 4-month range
 */
export function calculateFourMonthRange(centerDate?: string): {
  start: Date
  end: Date
} {
  // Use arrive date if provided, otherwise use today (all in UTC)
  const center = centerDate ? new Date(centerDate) : new Date()

  // Calculate 1 month before the center date (using UTC)
  const startDate = new Date(
    Date.UTC(center.getUTCFullYear(), center.getUTCMonth() - 1, 1),
  )

  // Calculate 2 months after the center date (so 4 months total: prev, current, next, next+1)
  const endDate = new Date(
    Date.UTC(center.getUTCFullYear(), center.getUTCMonth() + 3, 1),
  )

  // Ensure we don't fetch data before today (avoid past dates, using UTC)
  const today = new Date()
  const todayMonthStart = new Date(
    Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1),
  )

  const finalStartDate =
    startDate < todayMonthStart ? todayMonthStart : startDate

  return {
    start: finalStartDate,
    end: endDate,
  }
}

// Keep the old function for backwards compatibility but make it call the new one
// and trim the result to 3 months if needed
export function calculateThreeMonthRange(centerDate?: string): {
  start: Date
  end: Date
} {
  // Use arrive date if provided, otherwise use today (all in UTC)
  const center = centerDate ? new Date(centerDate) : new Date()

  // Calculate 1 month before the center date (using UTC)
  const startDate = new Date(
    Date.UTC(center.getUTCFullYear(), center.getUTCMonth() - 1, 1),
  )

  // Calculate 1 month after the center date (so 3 months total, using UTC)
  const endDate = new Date(
    Date.UTC(center.getUTCFullYear(), center.getUTCMonth() + 2, 1),
  )

  // Ensure we don't fetch data before today (avoid past dates, using UTC)
  const today = new Date()
  const todayMonthStart = new Date(
    Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1),
  )

  const finalStartDate =
    startDate < todayMonthStart ? todayMonthStart : startDate

  return {
    start: finalStartDate,
    end: endDate,
  }
}

/**
 * Calculate a 4-month date range centered around a specific month/year
 * Fetches: previous month, target month, next month, and next+1 month
 *
 * @param month - Target month (0-11)
 * @param year - Target year
 * @returns Object with start and end dates for the 4-month range
 */
export function calculateFourMonthRangeForMonth(
  month: number,
  year: number,
): { start: Date; end: Date } {
  // Calculate 1 month before the center date (using UTC)
  const startDate = new Date(Date.UTC(year, month - 1, 1))

  // Calculate 2 months after the center date (so 4 months total: prev, current, next, next+1)
  const endDate = new Date(Date.UTC(year, month + 3, 1))

  // Ensure we don't fetch data before today (avoid past dates, using UTC)
  const today = new Date()
  const todayMonthStart = new Date(
    Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1),
  )

  const finalStartDate =
    startDate < todayMonthStart ? todayMonthStart : startDate

  return {
    start: finalStartDate,
    end: endDate,
  }
}

// Keep the old function for backwards compatibility
export function calculateThreeMonthRangeForMonth(
  month: number,
  year: number,
): { start: Date; end: Date } {
  // Calculate 1 month before the center date (using UTC)
  const startDate = new Date(Date.UTC(year, month - 1, 1))

  // Calculate 1 month after the center date (so 3 months total, using UTC)
  const endDate = new Date(Date.UTC(year, month + 2, 1))

  // Ensure we don't fetch data before today (avoid past dates, using UTC)
  const today = new Date()
  const todayMonthStart = new Date(
    Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1),
  )

  const finalStartDate =
    startDate < todayMonthStart ? todayMonthStart : startDate

  return {
    start: finalStartDate,
    end: endDate,
  }
}

/**
 * Calculate a 3-month date range centered around arrive date (or today) as ISO strings
 *
 * @param arriveDate - Optional arrive date to center the range around
 * @returns Object with start and end dates as ISO strings
 */
export function calculateThreeMonthRangeIso(arriveDate?: string): {
  start: string
  end: string
} {
  const { start, end } = calculateThreeMonthRange(arriveDate)

  return {
    start: IsoDateFormatter.toIsoDate(start),
    end: IsoDateFormatter.toIsoDate(end),
  }
}

/**
 * Calculate an optimized date range for mini calendars based on months to display
 * This is used for mini calendars without navigation that only show a fixed number of months
 *
 * @param monthsToShow - Number of months to display (typically 2 for campsite detail view)
 * @param centerDate - Optional date to center around (arrive date or today)
 * @returns Object with start and end dates for the exact range needed
 */
export function calculateOptimizedMonthRange(
  monthsToShow: number,
  centerDate?: string,
): { start: Date; end: Date } {
  // Use arrive date if provided, otherwise use today (all in UTC)
  const center = centerDate ? new Date(centerDate) : new Date()

  // For mini calendars with dated searches, we show previous month + searched month
  // This matches the display behavior in BasePitchTypeCard
  // Example: Nov search shows Oct + Nov (not Nov + Dec)
  let startDate: Date
  let endDate: Date

  if (centerDate) {
    // Dated search: start from previous month
    startDate = new Date(
      Date.UTC(center.getUTCFullYear(), center.getUTCMonth() - 1, 1),
    )
    // End at current month + 1 (so for 2 months display, we get prev + current)
    endDate = new Date(
      Date.UTC(center.getUTCFullYear(), center.getUTCMonth() + 1, 1),
    )
  } else {
    // No dates: show current month + next months
    startDate = new Date(
      Date.UTC(center.getUTCFullYear(), center.getUTCMonth(), 1),
    )
    endDate = new Date(
      Date.UTC(center.getUTCFullYear(), center.getUTCMonth() + monthsToShow, 1),
    )
  }

  // Ensure we don't fetch data before today (avoid past dates, using UTC)
  const today = new Date()
  const todayMonthStart = new Date(
    Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1),
  )

  // If start date is adjusted forward, we need to adjust end date too
  // to ensure we still fetch the correct number of months
  if (startDate < todayMonthStart) {
    const monthsAdjusted =
      (todayMonthStart.getUTCFullYear() - startDate.getUTCFullYear()) * 12 +
      (todayMonthStart.getUTCMonth() - startDate.getUTCMonth())
    startDate = todayMonthStart
    // Extend end date by the same number of months we moved the start date forward
    endDate = new Date(
      Date.UTC(
        endDate.getUTCFullYear(),
        endDate.getUTCMonth() + monthsAdjusted,
        1,
      ),
    )
  }

  return {
    start: startDate,
    end: endDate,
  }
}
